// Окно «Выдать доступ сопровождению» (T053; contracts/ui.md, «Действия и подтверждения»;
// FR-023): кому, на сколько часов (1–24), причина (5–500 знаков) → «Выдать доступ на {N} ч».
// Кому — пользователи с ролью «сопровождение»: пока список грузится дольше 0,3 с — «Загрузка…» в
// поле и строка «Загружаем пользователей…» в меню; не загрузился — в меню строка с кодом и
// «Повторить», под полем выноска с причиной, номером операции и «Повторить»; никого нет — поле
// неактивно с причиной. Пустого меню нет (FR-040, FR-040а; T127, T159; Constitution X). Строки
// меню — сообщения DropdownMessage, а не неактивные варианты: текст читаем (T150). Проверка при
// отправке по той же схеме, что на сервере; ошибка под полем с примером, фокус — в первое поле с ошибкой;
// после первой отправки поле перепроверяется при вводе, и ошибка уходит, только когда значение
// прошло схему (FR-030, T128). Отказ сервера по полям — текст под полем, код и номер операции — в
// выноске над полями (T058). После выдачи — тост «Доступ выдан до {время}».
import {
  CreateSupportGrantBodySchema,
  SUPPORT_GRANT_MAX_HOURS,
  fieldErrorsFromIssues,
} from '@kp/contracts';
import {
  Button,
  DropdownMessage,
  Modal,
  ModalFooter,
  PickerField,
  TextAreaField,
  TextField,
  useDelayedFlag,
  useToast,
} from '@kp/ui';
import type { PickerOption } from '@kp/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { api, apiQuery, isApiError } from '../../api/client.ts';
import { FieldsRefusalCallout } from '../../app/FieldsRefusal.tsx';
import { errorView, hasFieldTexts, useErrorToast } from '../../app/errors.ts';
import type { FieldsRefusal } from '../../app/errors.ts';
import { formatMoment } from '../../app/format.ts';
import type { CreateModalProps } from '../../shell/context.tsx';
import { useTimeZone } from '../../shell/session.ts';
import { ListLoadError, useQueryFailure } from '../load-error.tsx';

type FieldName = 'supportUserId' | 'hours' | 'reason';
const FIELD_ORDER: readonly FieldName[] = ['supportUserId', 'hours', 'reason'];

export const DEFAULT_HOURS = 2;

const USERS_FAILED = 'Не удалось загрузить список сопровождения';

function mapFields(fields: Readonly<Record<string, string>>): Partial<Record<FieldName, string>> {
  const result: Partial<Record<FieldName, string>> = {};
  for (const field of FIELD_ORDER) {
    const text = fields[field];
    if (text) result[field] = text;
  }
  return result;
}

/** Часы из поля: целое число или NaN — схема ответит ошибкой с примером. */
function parseHours(text: string): number {
  return /^\d+$/.test(text.trim()) ? Number(text.trim()) : Number.NaN;
}

interface Values {
  supportUserId: string | null;
  hours: string;
  reason: string;
}

function bodyOf(values: Values) {
  return {
    supportUserId: values.supportUserId ?? '',
    hours: parseHours(values.hours),
    reason: values.reason,
  };
}

/** Ошибки полей по той же схеме, что на сервере. */
function validate(values: Values): Partial<Record<FieldName, string>> {
  const parsed = CreateSupportGrantBodySchema.safeParse(bodyOf(values));
  return parsed.success ? {} : mapFields(fieldErrorsFromIssues(parsed.error.issues));
}

export function GrantAccessModal({ open, onOpenChange, returnFocusRef }: CreateModalProps) {
  const toast = useToast();
  const showError = useErrorToast();
  const queryClient = useQueryClient();
  const timeZone = useTimeZone();
  const formId = useId();

  const [supportUserId, setSupportUserId] = useState<string | null>(null);
  const [hours, setHours] = useState(String(DEFAULT_HOURS));
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  /** Отказ сервера по полям: код и номер операции — в выноске над полями (T058). */
  const [refusal, setRefusal] = useState<FieldsRefusal<FieldName> | null>(null);
  /** Форму уже отправляли: дальше поля перепроверяются при вводе (FR-030). */
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const userRef = useRef<HTMLButtonElement>(null);
  const hoursRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  // Сопровождение — немного людей: список целиком, без поиска.
  const supportUsers = useQuery(
    apiQuery('users.list', { query: { kind: 'support', limit: 200, sort: 'fullName' } }),
  );
  const options: PickerOption<string>[] = (supportUsers.data?.items ?? []).map((user) => ({
    value: user.id,
    label: user.fullName,
    description: user.email,
  }));
  const noSupport = supportUsers.isSuccess && options.length === 0;
  // Загрузка видна, только если ответ дольше 0,3 с; поле не выключается, фокус остаётся в нём.
  const loadingUsers = useDelayedFlag(supportUsers.isPending);
  // Сбой — выноска под полем; на время «Повторить» она на месте со спиннером (T168).
  const {
    failure: usersError,
    retrying: usersRetrying,
    retry: retryUsers,
  } = useQueryFailure(supportUsers);
  // Пока ответа нет — в меню ни людей, ни строки; дольше 0,3 с — «Загружаем пользователей…»
  // (задержку ведёт сама строка). Сбой — строка с кодом и «Повторить»: другого пункта в меню нет;
  // повтор идёт — снова загрузка.
  let usersMessage: ReactNode = null;
  if (usersError && !usersRetrying) {
    usersMessage = (
      <DropdownMessage tone="error" code={usersError.code} onRetry={retryUsers}>
        {USERS_FAILED}
      </DropdownMessage>
    );
  } else if (supportUsers.isPending) {
    usersMessage = <DropdownMessage tone="loading">Загружаем пользователей…</DropdownMessage>;
  }

  const parsedHours = parseHours(hours);
  const hoursLabel =
    Number.isInteger(parsedHours) && parsedHours >= 1 && parsedHours <= SUPPORT_GRANT_MAX_HOURS
      ? `Выдать доступ на ${parsedHours} ч`
      : 'Выдать доступ';

  /**
   * После первой отправки — проверка при вводе: ошибка поля уходит, только когда значение прошло
   * схему, и меняется, если стала другой.
   */
  const recheck = (field: FieldName, changed: Partial<Values>) => {
    if (!submitted) return;
    const found = validate({ supportUserId, hours, reason, ...changed });
    setErrors((current) => ({ ...current, [field]: found[field] }));
  };

  const showErrors = (found: Partial<Record<FieldName, string>>) => {
    setErrors(found);
    const first = FIELD_ORDER.find((field) => found[field]);
    const target = {
      supportUserId: userRef.current,
      hours: hoursRef.current,
      reason: reasonRef.current,
    };
    if (first) target[first]?.focus();
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (pending) return;
    setSubmitted(true);
    setRefusal(null);
    const parsed = CreateSupportGrantBodySchema.safeParse(bodyOf({ supportUserId, hours, reason }));
    if (!parsed.success) {
      showErrors(mapFields(fieldErrorsFromIssues(parsed.error.issues)));
      return;
    }
    setPending(true);
    let validUntil: string;
    try {
      validUntil = (await api('supportAccess.grant', { body: parsed.data })).validUntil;
    } catch (error) {
      setPending(false);
      const fields = isApiError(error, 'REC-007') ? mapFields(error.fields) : {};
      if (hasFieldTexts(fields)) {
        showErrors(fields);
        setRefusal({ fields, view: errorView(error) });
      } else {
        // Отказ не попал ни в одно поле — тост целиком: причина, шаг, код и номер операции.
        showError(error, () => void submit());
      }
      return;
    }
    setPending(false);
    void queryClient.invalidateQueries({ queryKey: ['api', 'supportAccess.list'] });
    void queryClient.invalidateQueries({ queryKey: ['api', 'notifications.list'] });
    toast.ok(`Доступ выдан до ${formatMoment(validUntil, timeZone)}`);
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Выдать доступ сопровождению"
      description="Сопровождение увидит записи по всем протоколам. Записи о пользователях и доступе останутся закрыты"
      returnFocusRef={returnFocusRef}
      onSubmitShortcut={() => void submit()}
      footer={
        <ModalFooter
          submitHint
          primary={
            <Button type="submit" form={formId} variant="primary" loading={pending}>
              {hoursLabel}
            </Button>
          }
        />
      }
    >
      <form id={formId} className="t-col" noValidate onSubmit={(event) => void submit(event)}>
        <FieldsRefusalCallout refusal={refusal} errors={errors} />
        <PickerField<string>
          ref={userRef}
          label="Кому"
          required
          autoFocus
          placeholder={loadingUsers ? 'Загрузка…' : 'Выберите пользователя'}
          value={supportUserId}
          options={options}
          message={usersMessage}
          onChange={(value) => {
            setSupportUserId(value);
            recheck('supportUserId', { supportUserId: value });
          }}
          hint={loadingUsers ? 'Загружаем пользователей с ролью «сопровождение»' : undefined}
          disabled={noSupport}
          disabledReason="Пользователей с ролью «сопровождение» нет. Назначьте роль в разделе «Пользователи»"
          error={errors.supportUserId}
        />
        {usersError ? (
          <ListLoadError
            what={USERS_FAILED}
            view={usersError}
            retrying={usersRetrying}
            onRetry={retryUsers}
          />
        ) : null}
        <TextField
          ref={hoursRef}
          label="На сколько часов"
          type="number"
          inputMode="numeric"
          min={1}
          max={SUPPORT_GRANT_MAX_HOURS}
          step={1}
          required
          value={hours}
          hint="От 1 до 24 часов; доступ закончится сам"
          error={errors.hours}
          onChange={(event) => {
            setHours(event.target.value);
            recheck('hours', { hours: event.target.value });
          }}
        />
        <TextAreaField
          ref={reasonRef}
          label="Причина"
          required
          rows={3}
          maxLength={500}
          placeholder="например, Разбор ошибки загрузки протокола"
          value={reason}
          hint="Видна в списке доступа и в журнале действий"
          error={errors.reason}
          onChange={(event) => {
            setReason(event.target.value);
            recheck('reason', { reason: event.target.value });
          }}
        />
      </form>
    </Modal>
  );
}
